<template>
    <div class="columns" v-if="initDone">
        <div class="column" v-if="coupons.length > 0">
            <div class="box" v-for="coupon in coupons" :key="coupon.id">
                <div class="columns is-vcentered">
                    <div class="column">
                        <h5 class="is-size-5 mb-2 has-text-weight-bold">
                            <span class="icon"><i class="fas fa-ticket-alt"></i></span>
                            <span>{{ coupon.code }}</span>
                            <button
                                class="button is-small ml-3 is-glass"
                                @click="copyCouponCode(coupon.code)"
                                :title="translate('common.copy')"
                            >
                                <span class="icon is-small"><i class="fas fa-copy"></i></span>
                            </button>
                        </h5>

                        <div style="line-height: 1.7">
                            <div>
                                <span class="tag is-success" v-if="coupon.active && !isExpired(coupon)">
                                    {{ translate("coupons.status_active") }}
                                </span>
                                <span class="tag is-warning" v-else-if="isExpired(coupon)">
                                    {{ translate("coupons.status_expired") }}
                                </span>
                                <span class="tag is-white" v-else>
                                    {{ translate("coupons.status_inactive") }}
                                </span>
                            </div>

                            <div class="mt-2">
                                <span class="icon-text">
                                    <span class="icon"><i class="fas fa-percentage"></i></span>
                                    <span class="is-description">{{ translate("coupons.discount") }}:</span>
                                </span>
                                <span class="has-text-weight-bold ml-2">{{ coupon.discountPercentage }}%</span>
                            </div>

                            <div class="mt-2">
                                <span class="icon-text">
                                    <span class="icon"><i class="fas fa-user-tag"></i></span>
                                    <span class="is-description">{{ translate("coupons.usage_type") }}:</span>
                                </span>
                                <span class="ml-2">
                                    {{
                                        coupon.usageType === "personal"
                                            ? translate("coupons.usage_type_personal")
                                            : translate("coupons.usage_type_shared")
                                    }}
                                </span>
                            </div>

                            <div v-if="coupon.validUntil">
                                <span class="icon-text">
                                    <span class="icon"><i class="fas fa-calendar"></i></span>
                                    <span class="is-description">{{ translate("coupons.validity_period") }}:</span>
                                </span>
                                <span class="ml-2">
                                    {{ formatValidityPeriod(coupon) }}
                                </span>
                            </div>

                            <div>
                                <span class="icon-text">
                                    <span class="icon"><i class="fas fa-users"></i></span>
                                    <span class="is-description">{{ translate("coupons.total_usage") }}:</span>
                                </span>
                                <span class="ml-2">
                                    {{ coupon.usedCount }}
                                    <span v-if="coupon.maxUses">({{ translate("coupons.max_per_user") }}: {{ coupon.maxUses }})</span>
                                    <span v-else>{{ translate("coupons.unlimited") }}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="column is-narrow">
                        <div class="buttons are-small">
                            <button class="button" @click="viewUsageDetails(coupon)" v-if="coupon.usedCount > 0">
                                <span class="icon"><i class="fas fa-chart-bar"></i></span>
                                <span>{{ translate("coupons.view_usage") }}</span>
                            </button>
                            <button class="button" @click="editCoupon(coupon)">
                                <span class="icon"><i class="fas fa-edit"></i></span>
                                <span>{{ translate("common.edit") }}</span>
                            </button>
                            <button class="button is-danger" @click="deleteCoupon(coupon.id)">
                                <span class="icon"><i class="fas fa-trash-alt"></i></span>
                                <span>{{ translate("common.delete") }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="column is-6 is-offset-3">
            <Notification class="is-info has-text-centered">
                <span class="icon"><i class="fas fa-info-circle"></i></span>
                <span>{{ translate("coupons.no_coupons") }}</span>
            </Notification>
        </div>
    </div>
    <div v-else class="columns is-mobile is-centered">
        <LoadingSpinner2 class="has-added-margin" />
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted } from "vue";
    import { translate } from "../../Common/translations";
    import Notification from "../../Common/Notification.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import couponsApi, { Coupon, CouponWithUsageCount } from "../../Api/coupons";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";

    const props = defineProps<{
        eventId: number;
    }>();

    const emit = defineEmits<{
        editCoupon: [coupon: Coupon];
        viewUsage: [coupon: Coupon];
    }>();

    const initDone = ref(false);
    const coupons = ref<CouponWithUsageCount[]>([]);

    const fetchCoupons = () => {
        return couponsApi.getListing(props.eventId).then(result => {
            coupons.value = result;
        });
    };

    const refresh = () => {
        initDone.value = false;
        return fetchCoupons()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    };

    const deleteCoupon = (couponId: number) => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        return couponsApi
            .deleteCoupon(props.eventId, couponId)
            .then(() => {
                successToast(translate("coupons.deleted_successfully"));
                return refresh();
            })
            .catch(handleError);
    };

    const copyCouponCode = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            successToast(translate("coupons.code_copied"));
        });
    };

    const formatValidityPeriod = (coupon: Coupon): string => {
        if (coupon.validUntil) {
            return translate("common.to") + " " + formatDate(coupon.validUntil);
        }
        return "";
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString();
    };

    const isExpired = (coupon: Coupon): boolean => {
        if (!coupon.validUntil) {
            return false;
        }
        return new Date(coupon.validUntil) < new Date();
    };

    const editCoupon = (coupon: Coupon) => {
        emit("editCoupon", coupon);
    };

    const viewUsageDetails = (coupon: Coupon) => {
        emit("viewUsage", coupon);
    };

    onMounted(() => {
        refresh();
    });

    defineExpose({ refresh });
</script>

<style scoped></style>
